<script setup>
import { ref, shallowRef } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiAuthLayout from '@/components/ui/UiAuthLayout.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { homePathFor, loadSession } from '@/api/session';
import { email, required } from '@/utils/validation';

const store = useStore();
const router = useRouter();
const formRef = ref(null);
const form = ref({ email: '', password: '' });
const showPassword = shallowRef(false);
const loading = shallowRef(false);
const error = shallowRef('');

async function signIn() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  loading.value = true;
  error.value = '';
  try {
    const body = await apiRequest('post', '/auth/login', {
      email: form.value.email.trim(),
      password: form.value.password,
    });
    if ([200, 201, 202].includes(Number(body?.status))) {
      localStorage.setItem('token', body.token);
      store.commit('setExpiryDate');
      // Load the user and go straight to their dashboard. (This used to
      // reload the whole page, which flashed white and then showed the login
      // form again before the dashboard.)
      const user = await loadSession(store);
      if (user) {
        await router.replace(homePathFor(user));
        return;
      }
      localStorage.removeItem('token');
      error.value = 'Signed in, but your account could not be loaded. Please try again.';
      loading.value = false;
      return;
    }
    error.value = body?.message || body?.msg || 'Incorrect email or password.';
  } catch (e) {
    error.value = errorMessage(e, 'Could not sign in. Please try again.');
  }
  loading.value = false;
}
</script>

<template>
  <UiAuthLayout title="Sign in to your account" description="Enter your email and password below to sign in.">
    <v-form ref="formRef" class="form-stack" @submit.prevent="signIn">
      <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

      <UiFormField label="Email address" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.email"
          type="email"
          autocomplete="email"
          placeholder="email@example.com"
          :rules="[required, email]"
          hide-details="auto"
          autofocus
        />
      </UiFormField>

      <UiFormField label="Password">
        <template #label-end>
          <router-link to="/forgot-password" class="forgot">Forgot password?</router-link>
        </template>
        <template #default="{ id }">
          <v-text-field
            :id="id"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
            placeholder="Password"
            :rules="[required]"
            :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
            hide-details="auto"
            @click:append-inner="showPassword = !showPassword"
          />
        </template>
      </UiFormField>

      <v-btn type="submit" color="primary" block :loading="loading">Sign in</v-btn>
    </v-form>

    <template #footer>
      Don't have an account? <router-link to="/register">Sign up</router-link>
    </template>
  </UiAuthLayout>
</template>

<style scoped>
.forgot {
  font-size: 13px;
  color: var(--muted-foreground);
  text-decoration: underline;
  text-underline-offset: 4px;
}
.forgot:hover {
  color: var(--foreground);
}
</style>
